/**
 * Predictive search — Shopify Search & Discovery via Section Rendering API.
 * Fetches /search/suggest?section_id=predictive-search and injects Liquid HTML.
 */
(() => {
  const dialog = document.querySelector('[data-predictive-search]');
  if (!dialog) return;

  const configEl = document.querySelector('[data-predictive-search-config]');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  const form = dialog.querySelector('[data-predictive-search-form]');
  const input = dialog.querySelector('[data-predictive-search-input]');
  const panel = dialog.querySelector('[data-predictive-search-panel]');
  const resultsEl = dialog.querySelector('[data-predictive-search-results]');
  const closeBtn = dialog.querySelector('[data-predictive-search-close]');
  const openBtns = document.querySelectorAll('[data-predictive-search-open]');

  let debounceTimer;
  let activeController;
  let activeIndex = -1;

  const setExpanded = (expanded) => {
    input?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const getOptions = () => Array.from(resultsEl.querySelectorAll('.predictive-search__option'));

  const clearActive = () => {
    getOptions().forEach((el) => el.classList.remove('predictive-search__option--active'));
    input?.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  };

  const setActive = (index) => {
    const options = getOptions();
    if (!options.length) {
      clearActive();
      return;
    }

    activeIndex = ((index % options.length) + options.length) % options.length;
    options.forEach((el, i) => {
      const isActive = i === activeIndex;
      el.classList.toggle('predictive-search__option--active', isActive);
      if (isActive) {
        const optionId = el.closest('[role="option"]')?.id || el.id;
        if (optionId) input?.setAttribute('aria-activedescendant', optionId);
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  const showStatus = (message) => {
    resultsEl.innerHTML = `<p class="py-10 text-center text-sm text-gray-500" role="status">${message}</p>`;
    panel.hidden = false;
    setExpanded(true);
    clearActive();
  };

  const closeResults = () => {
    panel.hidden = true;
    resultsEl.innerHTML = '';
    setExpanded(false);
    clearActive();
  };

  const fetchSuggestions = (query) => {
    if (activeController) activeController.abort();

    const term = query.trim();
    if (!term.length) {
      closeResults();
      return;
    }

    activeController = new AbortController();
    const params = new URLSearchParams({
      q: term,
      'resources[limit_scope]': 'each',
      'resources[limit]': '4',
      'resources[options][unavailable_products]': 'last',
      section_id: config.sectionId,
    });

    showStatus(config.strings.loading);

    fetch(`${config.suggestUrl}?${params}`, { signal: activeController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector(`#shopify-section-${config.sectionId}`);
        const markup = section?.querySelector('[data-predictive-search-results-markup]') || section;
        if (!markup || !markup.innerHTML.trim()) {
          closeResults();
          return;
        }
        resultsEl.innerHTML = markup.innerHTML;
        panel.hidden = false;
        setExpanded(true);
        clearActive();
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        showStatus(config.strings.error);
      });
  };

  const open = () => {
    if (typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
    }
    input.value = '';
    closeResults();
    requestAnimationFrame(() => input.focus());
  };

  const close = () => {
    if (dialog.open) dialog.close();
    if (activeController) activeController.abort();
    closeResults();
  };

  openBtns.forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      open();
    });
  });

  closeBtn?.addEventListener('click', close);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  dialog.addEventListener('close', () => {
    if (activeController) activeController.abort();
    closeResults();
  });

  input?.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchSuggestions(input.value), 250);
  });

  input?.addEventListener('keydown', (event) => {
    const options = getOptions();
    if (!options.length && event.key !== 'Escape') return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive(activeIndex - 1);
        break;
      case 'Enter':
        if (activeIndex >= 0 && options[activeIndex]) {
          event.preventDefault();
          options[activeIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        if (!panel.hidden && input.value.trim()) {
          closeResults();
          input.value = '';
        } else {
          close();
        }
        break;
      default:
        break;
    }
  });

  form?.addEventListener('submit', (event) => {
    if (!input.value.trim()) event.preventDefault();
  });

  // Active option highlight styles (injected once).
  if (!document.getElementById('predictive-search-active-style')) {
    const style = document.createElement('style');
    style.id = 'predictive-search-active-style';
    style.textContent = `
      .predictive-search__option--active {
        background-color: var(--color-gray-50, #f7f6f4) !important;
        outline: 2px solid var(--color-indigo-600, #2c2926);
        outline-offset: -2px;
      }
      [data-predictive-search-results] mark {
        background: transparent;
        color: inherit;
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);
  }
})();
